import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import * as authApi from '@/features/auth/api';
import { ApiError } from '@/lib/api/client';

const GENERIC_INVALID_MESSAGE = 'Tautan ini tidak valid, sudah digunakan, atau sudah kedaluwarsa.';

type VerifyState = 'verifying' | 'success' | 'error' | 'missing-token';

/**
 * `POST /auth/verify-email` (Phase G-7, Job 8). Public - `authenticate` is
 * not on this backend route, so this page works for a signed-out visitor
 * clicking an emailed link and an already-signed-in user alike. Captures
 * `?token=` on mount and immediately scrubs it from the URL/history (same
 * discipline as reset-password-page.tsx / accept-invitation-page.tsx), then
 * verifies automatically - there is no form, only a status.
 */
export function VerifyEmailPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const attempted = useRef(false);
  const [state, setState] = useState<VerifyState>('verifying');
  const [errorMessage, setErrorMessage] = useState(GENERIC_INVALID_MESSAGE);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken !== null && urlToken.length > 0) {
      void navigate('/verify-email', { replace: true });
      if (attempted.current) {
        return;
      }
      attempted.current = true;
      authApi
        .verifyEmail(urlToken)
        .then(() => {
          setState('success');
        })
        .catch((caught: unknown) => {
          setErrorMessage(
            caught instanceof ApiError && caught.code === 'INVALID_VERIFICATION_TOKEN'
              ? GENERIC_INVALID_MESSAGE
              : caught instanceof ApiError
                ? caught.message
                : 'Terjadi kesalahan. Silakan coba lagi.',
          );
          setState('error');
        });
      return;
    }
    setState('missing-token');
    // Only runs once per mount: the token, once captured, is scrubbed from
    // the URL above, so this effect must not re-fire on the resulting
    // navigation and re-read a now-clean URL as "missing".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === 'verifying') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4" role="status">
        <Skeleton className="h-8 w-48" />
      </div>
    );
  }

  if (state === 'missing-token' || state === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <h1 className="sr-only">Tidak Dapat Memverifikasi Email</h1>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Tidak Dapat Memverifikasi Email</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>
                {state === 'missing-token' ? GENERIC_INVALID_MESSAGE : errorMessage}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <h1 className="sr-only">Email Terverifikasi</h1>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Email Terverifikasi</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Alert>
            <AlertTitle>Berhasil</AlertTitle>
            <AlertDescription>Alamat email Anda telah diverifikasi.</AlertDescription>
          </Alert>
          <Button asChild>
            <Link to="/">Lanjutkan ke Mahfuz</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
